import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { accent, surface, border, textMuted, font, shadowMed } from "../../layouts/schoolPortal/schoolPortalTheme";

const CALENDLY_URL = "https://calendly.com/xair-isaac/ceo";
const CALENDLY_SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";

function useCalendlyScript() {
  const [ready, setReady] = useState(!!window.Calendly);

  useEffect(() => {
    if (window.Calendly) {
      setReady(true);
      return;
    }
    const existing = document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => setReady(true));
      return;
    }
    const script = document.createElement("script");
    script.src = CALENDLY_SCRIPT_SRC;
    script.async = true;
    script.onload = () => setReady(true);
    document.body.appendChild(script);
  }, []);

  return ready;
}

export default function SchoolBookMeeting() {
  const calendlyReady = useCalendlyScript();
  const widgetRef = useRef(null);

  // Calendly's widget.js only auto-scans the DOM for `.calendly-inline-widget`
  // elements once, right when it loads — in a React app the container usually
  // doesn't exist in the DOM yet at that point, so the auto-scan finds nothing
  // and the box stays permanently empty. Initializing it explicitly once the
  // container is actually mounted avoids that race.
  useEffect(() => {
    if (calendlyReady && widgetRef.current && window.Calendly) {
      widgetRef.current.innerHTML = "";
      window.Calendly.initInlineWidget({
        url: `${CALENDLY_URL}?hide_gdpr_banner=1&primary_color=c01e2e`,
        parentElement: widgetRef.current,
      });
    }
  }, [calendlyReady]);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", p: { xs: 1.5, sm: 2 } }}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: "14px",
          overflow: "hidden",
          backgroundColor: surface,
          border: `1px solid ${border}`,
          boxShadow: shadowMed,
          position: "relative",
        }}
      >
        <div ref={widgetRef} style={{ width: "100%", height: "100%", minWidth: 320 }} />
        {!calendlyReady && (
          <Box sx={{
            position: "absolute", inset: 0, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 2, backgroundColor: surface,
          }}>
            <CircularProgress size={28} sx={{ color: accent }} />
            <Typography sx={{ fontSize: "13px", color: textMuted, fontFamily: font }}>Loading calendar…</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
